import { useEffect, useMemo, useRef, useState } from 'react'
import 'quill/dist/quill.snow.css'
import 'quill-mention/dist/quill.mention.css'
import Quill from 'quill'
import { MentionBlot, Mention } from 'quill-mention'
import useApiService from 'services/api'
import { useAuth } from 'context/authentication/useAuth'
import './Editor.css'

// Register Quill modules
Quill.register({ 'blots/mention': MentionBlot, 'modules/mention': Mention })

export interface EditorProps {
  value: string
  onChange?: (value: string | undefined) => void
}

export interface CommonEditorProps {
  theme?: 'snow' | 'bubble'
  placeholder?: string
  label?: string
  optional?: boolean
  additionalToolbarButtons?: React.ReactNode[]
  /** Content rendered before the toolbar (e.g. a Send button). */
  toolbarPrefix?: React.ReactNode
  /** Content rendered after the toolbar, pushed to the right (e.g. a Cancel link). */
  toolbarSuffix?: React.ReactNode
  /** Additional className applied to the toolbar wrapper (e.g. background color). */
  toolbarClassName?: string
  /** Content rendered between the body editor and the toolbar (e.g. a
   *  warning banner). Renders as a sibling of the toolbar, so sticky
   *  positioning and styling on the toolbar are unaffected. */
  aboveToolbar?: React.ReactNode
  readOnly?: boolean
  disableToolbar?: boolean
  enableMentions?: boolean
  className?: string
  borderless?: boolean
  disabled?: boolean
  sublabel?: string
  helperText?: string
  noMargin?: boolean
  error?: { message?: string }
}

export interface EditorCoreState {
  uniqueId: string
  quillEditor: Quill | null
  isFocused: boolean
  hasValue: boolean
  /** Ref tracking current cursor position — updated on selection/text changes. */
  cursorPositionRef: React.RefObject<number>
}

export function useEditorCore(props: EditorProps & CommonEditorProps) {
  const { onChange, value, readOnly, disabled } = props

  const [isFocused, setIsFocused] = useState(false)
  const [quillEditor, setQuillEditor] = useState<Quill | null>(null)
  const cursorPositionRef = useRef(0)
  const hasValue =
    value !== '' && value !== '<p><br></p>' && value !== '<p></p>'

  // We need to generate a unique ID for the toolbar container since multiple can coexist on the same page
  const uniqueId = useMemo(() => Math.random().toString(36).substring(7), [])

  const api = useApiService()
  const { data: users } = api.useGetUserOptions({
    onlyActive: true,
    withNoneOption: false,
  })
  const { user } = useAuth()

  const onChangeHandler = (value: string) => {
    let newValue: string = value
    if (newValue === '<p><br></p>') {
      // empty string on the WYSIWYG editor
      newValue = ''
    }
    onChange?.(newValue)
  }

  useEffect(() => {
    if (!users) return

    const userOptions = users
      ?.filter((u) => u.value !== user?.id)
      .map((u) => ({
        id: u.value,
        value: u.label,
      }))

    const modules: Record<string, unknown> = {
      toolbar: props.disableToolbar
        ? false
        : {
            container: `#toolbar-${uniqueId}`,
          },
    }

    if (props.enableMentions) {
      modules.mention = {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@'],
        source: function (searchTerm: string, renderList: any) {
          const MAX_OPTIONS = 5
          const values = userOptions ?? []

          if (searchTerm.length === 0) {
            renderList(values.slice(0, MAX_OPTIONS), searchTerm)
          } else {
            const matches = []
            for (let i = 0; i < values.length; i++) {
              if (
                values[i].value.toLowerCase().includes(searchTerm.toLowerCase())
              ) {
                matches.push(values[i])
              }
            }
            renderList(matches.slice(0, MAX_OPTIONS), searchTerm)
          }
        },
      }
    }

    const editor = new Quill(`#editor-${uniqueId}`, {
      readOnly: readOnly || disabled,
      theme: props.theme ?? 'snow',
      placeholder: props.placeholder ?? 'Add a new note here',
      modules,
    })

    setQuillEditor(editor)

    editor.on('text-change', () => {
      onChangeHandler(editor.getSemanticHTML())
      const sel = editor.getSelection()
      if (sel) cursorPositionRef.current = sel.index + sel.length
    })

    editor.on(
      'selection-change',
      (range: { index: number; length: number } | null) => {
        if (range) cursorPositionRef.current = range.index + range.length
      }
    )

    editor.root.addEventListener('focus', () => {
      setIsFocused(true)
    })

    editor.root.addEventListener('blur', () => {
      setIsFocused(false)
    })

    if (value) {
      editor.setContents(
        editor.clipboard.convert({
          html: value,
        })
      )
    }

    return () => {
      editor.root.removeEventListener('focus', () => {
        setIsFocused(true)
      })
      editor.root.removeEventListener('blur', () => {
        setIsFocused(false)
      })
    }
  }, [users])

  useEffect(() => {
    if (!quillEditor) return

    const currentContent = quillEditor.getSemanticHTML()
    const initialQuillValue = '<p><br></p>'
    const normalizedCurrentContent =
      currentContent === initialQuillValue ? '' : currentContent
    const normalizedValue = value || ''

    if (normalizedCurrentContent !== normalizedValue) {
      if (normalizedValue === '') {
        quillEditor.setText('')
      } else {
        quillEditor.setContents(
          quillEditor.clipboard.convert({
            html: normalizedValue,
          })
        )
      }
    }
  }, [value, quillEditor])

  return {
    uniqueId,
    quillEditor,
    isFocused,
    hasValue,
    cursorPositionRef,
  }
}

export function EditorToolbar(props: {
  uniqueId: string
  disableToolbar?: boolean
  additionalToolbarButtons?: React.ReactNode[]
}) {
  const { uniqueId, disableToolbar, additionalToolbarButtons } = props

  if (disableToolbar) {
    return null
  }

  return (
    <div id={`toolbar-${uniqueId}`}>
      <button className="ql-bold"></button>
      <button className="ql-italic"></button>
      <button className="ql-underline"></button>
      <button className="ql-strike"></button>
      <button className="ql-list" value="bullet"></button>
      <button className="ql-list" value="ordered"></button>
      {/* Custom attachment button */}
      {additionalToolbarButtons && additionalToolbarButtons}
    </div>
  )
}
